export interface GalleryImage {
  src: string;
  alt: string;
}

export interface ExperienceFaq {
  q: string;
  a: string;
}

export interface ExperienceDetail {
  slug: string;
  title: string;
  emoji: string;
  tagline: string;
  badge: string;
  heroImage: string;
  duration: string;
  groupSize: string;
  bestSeason: string;
  location: string;
  priceFrom: string;
  overview: string[];
  gallery: GalleryImage[];
  whatYouWillDo: string[];
  includes: string[];
  goodToKnow: string[];
  faqs: ExperienceFaq[];
}

export const experienceDetails: Record<string, ExperienceDetail> = {
  "farmer-for-a-day": {
    slug: "farmer-for-a-day",
    title: "Farmer for a Day",
    emoji: "🌾",
    tagline: "A day in the life of a farmer",
    badge: "Most Loved",
    heroImage:
      "https://readdy.ai/api/search-image?query=Indian%20farmer%20walking%20through%20lush%20green%20paddy%20fields%20at%20sunrise%20carrying%20a%20hoe%2C%20soft%20mist%2C%20fresh%20green%20light%2C%20painterly%20stylized%20earthy%20green%20tones%2C%20serene%20rural%20atmosphere%2C%20flooded%20with%20golden%20sunrise%20light%2C%20warm%20amber%20sunrays%20and%20soft%20glow%2C%20misty%20atmospheric%20haze%2C%20deep%20rich%20shadows%2C%20vivid%20saturated%20lush%20green%2C%20high%20contrast%20cinematic%20golden%20hour%20richness%2C%20ultra%20detailed&nocache=true&width=1920&height=1080&seq=exp-farmer-day-hero&orientation=landscape",
    duration: "Full day (approx. 8 hours)",
    groupSize: "2 – 20 people",
    bestSeason: "Year-round • best Oct–Feb",
    location: "Konaseema farmlands, Andhra Pradesh",
    priceFrom: "₹1,600 / person",
    overview: [
      "Spend a full day living the rhythm of a working farm. You will arrive before the heat sets in, share a morning cup of chai with the farming family, and then head out into the fields alongside them.",
      "There is no script here — the day follows the real work of the season. Depending on when you visit, you might help transplant seedlings, guide water through the irrigation channels, tend to coconut and banana trees, or bring in the harvest.",
      "By evening you will understand something simple but powerful: the food on your plate is the result of a farmer's entire day, every single day.",
    ],
    gallery: [
      {
        src: "https://readdy.ai/api/search-image?query=Close%20up%20of%20weathered%20farmer%20hands%20planting%20young%20rice%20seedlings%20into%20wet%20paddy%20soil%2C%20sunlight%2C%20fresh%20earthy%20tones%2C%20painterly%2C%20flooded%20with%20golden%20sunrise%20light%2C%20warm%20amber%20sunrays%20and%20soft%20glow%2C%20misty%20atmospheric%20haze%2C%20deep%20rich%20shadows%2C%20vivid%20saturated%20lush%20green%2C%20high%20contrast%20cinematic%20golden%20hour%20richness%2C%20ultra%20detailed&nocache=true&width=1200&height=900&seq=exp-farmerday-g1&orientation=landscape",
        alt: "Farmer hands planting rice seedlings in a paddy field",
      },
      {
        src: "https://readdy.ai/api/search-image?query=Traditional%20South%20Indian%20farming%20family%20sharing%20a%20meal%20served%20on%20banana%20leaves%20in%20a%20rustic%20village%20home%2C%20fresh%20light%2C%20painterly%2C%20flooded%20with%20golden%20sunrise%20light%2C%20warm%20amber%20sunrays%20and%20soft%20glow%2C%20misty%20atmospheric%20haze%2C%20deep%20rich%20shadows%2C%20vivid%20saturated%20lush%20green%2C%20high%20contrast%20cinematic%20golden%20hour%20richness%2C%20ultra%20detailed&nocache=true&width=1200&height=900&seq=exp-farmerday-g2&orientation=landscape",
        alt: "Farming family sharing a traditional meal on banana leaves",
      },
      {
        src: "https://readdy.ai/api/search-image?query=Bullock%20cart%20moving%20along%20a%20narrow%20mud%20path%20between%20green%20fields%20in%20rural%20India%2C%20soft%20evening%20light%2C%20painterly%20illustration%2C%20flooded%20with%20golden%20sunrise%20light%2C%20warm%20amber%20sunrays%20and%20soft%20glow%2C%20misty%20atmospheric%20haze%2C%20deep%20rich%20shadows%2C%20vivid%20saturated%20lush%20green%2C%20high%20contrast%20cinematic%20golden%20hour%20richness%2C%20ultra%20detailed&nocache=true&width=1200&height=900&seq=exp-farmerday-g3&orientation=landscape",
        alt: "Bullock cart on a mud path between green fields",
      },
      {
        src: "https://readdy.ai/api/search-image?query=Small%20group%20of%20visitors%20listening%20to%20a%20farmer%20explaining%20crops%20in%20a%20green%20field%2C%20sunny%20day%2C%20painterly%20earthy%20tones%2C%20flooded%20with%20golden%20sunrise%20light%2C%20warm%20amber%20sunrays%20and%20soft%20glow%2C%20misty%20atmospheric%20haze%2C%20deep%20rich%20shadows%2C%20vivid%20saturated%20lush%20green%2C%20high%20contrast%20cinematic%20golden%20hour%20richness%2C%20ultra%20detailed&nocache=true&width=1200&height=900&seq=exp-farmerday-g4&orientation=landscape",
        alt: "Visitors learning from a farmer in a green field",
      },
      {
        src: "https://readdy.ai/api/search-image?query=Wicker%20basket%20overflowing%20with%20freshly%20harvested%20vegetables%20and%20greens%20placed%20on%20farm%20soil%2C%20morning%20light%2C%20painterly%2C%20flooded%20with%20golden%20sunrise%20light%2C%20warm%20amber%20sunrays%20and%20soft%20glow%2C%20misty%20atmospheric%20haze%2C%20deep%20rich%20shadows%2C%20vivid%20saturated%20lush%20green%2C%20high%20contrast%20cinematic%20golden%20hour%20richness%2C%20ultra%20detailed&nocache=true&width=1200&height=900&seq=exp-farmerday-g5&orientation=landscape",
        alt: "Basket of freshly harvested vegetables on farm soil",
      },
    ],
    whatYouWillDo: [
      "Share morning chai and stories with the farming family",
      "Walk the fields and learn how each crop is grown",
      "Join a real seasonal farming activity with your own hands",
      "Learn how water moves through the irrigation channels",
      "Pick fresh produce straight from the soil",
      "Sit down to a traditional home-cooked farm meal",
    ],
    includes: [
      "Experienced local farmer host and guide",
      "All farm activities and tools",
      "Traditional breakfast, lunch and evening snack",
      "Drinking water and filter coffee",
      "Basic sun hat and farm gloves",
      "Photos of your day shared by the host",
    ],
    goodToKnow: [
      "Wear comfortable clothes you don't mind getting muddy",
      "Closed shoes or sandals work best on the farm",
      "Children above 6 are welcome with a guardian",
      "Activities shift with the season and weather",
    ],
    faqs: [
      {
        q: "Do I need any farming experience?",
        a: "Not at all. Everything is guided by the farmer host and adjusted to what you are comfortable doing.",
      },
      {
        q: "What should I carry?",
        a: "Comfortable clothes, a change of clothes, sunscreen and a water bottle. We provide gloves and a hat.",
      },
      {
        q: "Is the experience suitable for young children?",
        a: "Yes, children above 6 enjoy it a lot. Children stay with their guardian throughout the day.",
      },
    ],
  },

  "soil-to-plate": {
    slug: "soil-to-plate",
    title: "Soil to Plate",
    emoji: "🌱",
    tagline: "The journey of food",
    badge: "Food Journey",
    heroImage:
      "https://readdy.ai/api/search-image?query=Freshly%20harvested%20vegetables%20and%20grains%20arranged%20on%20a%20rustic%20wooden%20table%20beside%20garden%20soil%2C%20farm%20to%20table%20concept%2C%20fresh%20natural%20light%2C%20painterly%20stylized%2C%20flooded%20with%20golden%20sunrise%20light%2C%20warm%20amber%20sunrays%20and%20soft%20glow%2C%20misty%20atmospheric%20haze%2C%20deep%20rich%20shadows%2C%20vivid%20saturated%20lush%20green%2C%20high%20contrast%20cinematic%20golden%20hour%20richness%2C%20ultra%20detailed&nocache=true&width=1920&height=1080&seq=exp-soilplate-hero&orientation=landscape",
    duration: "Half day (approx. 5 hours)",
    groupSize: "2 – 25 people",
    bestSeason: "Jun – Mar",
    location: "Konaseema farms & village kitchens",
    priceFrom: "₹1,900 / person",
    overview: [
      "Follow a single ingredient from the moment it is in the ground to the moment it reaches your plate. This experience is a slow, sensory journey through soil, crops and traditional cooking.",
      "You will start in the field, understanding the soil and how crops are cultivated, then move to harvesting — picking the very vegetables that will become your meal.",
      "The day ends around a village kitchen stove, where you learn a traditional recipe and then sit down to eat what you grew, picked and cooked together.",
    ],
    gallery: [
      {
        src: "https://readdy.ai/api/search-image?query=Rich%20dark%20soil%20being%20held%20in%20cupped%20hands%20with%20a%20young%20green%20sprout%2C%20close%20up%2C%20fresh%20light%2C%20painterly%2C%20flooded%20with%20golden%20sunrise%20light%2C%20warm%20amber%20sunrays%20and%20soft%20glow%2C%20misty%20atmospheric%20haze%2C%20deep%20rich%20shadows%2C%20vivid%20saturated%20lush%20green%2C%20high%20contrast%20cinematic%20golden%20hour%20richness%2C%20ultra%20detailed&nocache=true&width=1200&height=900&seq=exp-soilplate-g1&orientation=landscape",
        alt: "Hands holding rich soil with a young sprout",
      },
      {
        src: "https://readdy.ai/api/search-image?query=Women%20cooking%20traditional%20South%20Indian%20meal%20in%20clay%20pots%20over%20a%20wood%20fire%20in%20a%20village%20kitchen%2C%20fresh%20painterly%20tones%2C%20flooded%20with%20golden%20sunrise%20light%2C%20warm%20amber%20sunrays%20and%20soft%20glow%2C%20misty%20atmospheric%20haze%2C%20deep%20rich%20shadows%2C%20vivid%20saturated%20lush%20green%2C%20high%20contrast%20cinematic%20golden%20hour%20richness%2C%20ultra%20detailed&nocache=true&width=1200&height=900&seq=exp-soilplate-g2&orientation=landscape",
        alt: "Traditional cooking in clay pots over a wood fire",
      },
      {
        src: "https://readdy.ai/api/search-image?query=Farmer%20and%20visitor%20harvesting%20fresh%20green%20vegetables%20in%20a%20sunlit%20field%2C%20baskets%20nearby%2C%20painterly%2C%20flooded%20with%20golden%20sunrise%20light%2C%20warm%20amber%20sunrays%20and%20soft%20glow%2C%20misty%20atmospheric%20haze%2C%20deep%20rich%20shadows%2C%20vivid%20saturated%20lush%20green%2C%20high%20contrast%20cinematic%20golden%20hour%20richness%2C%20ultra%20detailed&nocache=true&width=1200&height=900&seq=exp-soilplate-g3&orientation=landscape",
        alt: "Harvesting fresh vegetables in a sunlit field",
      },
      {
        src: "https://readdy.ai/api/search-image?query=Colorful%20traditional%20thali%20meal%20of%20rice%2C%20curries%20and%20vegetables%20served%20on%20banana%20leaf%2C%20top%20view%2C%20painterly%2C%20flooded%20with%20golden%20sunrise%20light%2C%20warm%20amber%20sunrays%20and%20soft%20glow%2C%20misty%20atmospheric%20haze%2C%20deep%20rich%20shadows%2C%20vivid%20saturated%20lush%20green%2C%20high%20contrast%20cinematic%20golden%20hour%20richness%2C%20ultra%20detailed&nocache=true&width=1200&height=900&seq=exp-soilplate-g4&orientation=landscape",
        alt: "Traditional thali meal served on a banana leaf",
      },
      {
        src: "https://readdy.ai/api/search-image?query=Hands%20shelling%20fresh%20peas%20and%20beans%20into%20a%20clay%20bowl%20on%20a%20village%20veranda%2C%20soft%20natural%20light%2C%20painterly%2C%20flooded%20with%20golden%20sunrise%20light%2C%20warm%20amber%20sunrays%20and%20soft%20glow%2C%20misty%20atmospheric%20haze%2C%20deep%20rich%20shadows%2C%20vivid%20saturated%20lush%20green%2C%20high%20contrast%20cinematic%20golden%20hour%20richness%2C%20ultra%20detailed&nocache=true&width=1200&height=900&seq=exp-soilplate-g5&orientation=landscape",
        alt: "Hands shelling fresh peas into a clay bowl",
      },
    ],
    whatYouWillDo: [
      "Learn to read the soil and understand what makes it healthy",
      "Walk the crop beds with the farmer who grows them",
      "Harvest seasonal vegetables for the day's meal",
      "Wash, clean and prepare the produce",
      "Cook a traditional recipe step by step",
      "Eat a farm-to-table meal you helped create",
    ],
    includes: [
      "Farmer-guided farm and soil walk",
      "Harvesting session with baskets",
      "Cooking class with a local cook",
      "Farm-to-table meal on banana leaf",
      "Recipe card to take home",
      "Drinking water and refreshments",
    ],
    goodToKnow: [
      "Vegetarian menu by default, seasonal and local",
      "Please tell us about allergies before you visit",
      "The harvest portion depends on the season",
      "Great for food lovers and curious cooks",
    ],
    faqs: [
      {
        q: "Do we cook the whole meal ourselves?",
        a: "You cook one or two signature dishes with the host and the rest is prepared together — it's a shared experience, not a test.",
      },
      {
        q: "Can the menu be customised?",
        a: "Yes, we can plan a fully vegetarian, vegan or mild menu if you tell us in advance.",
      },
      {
        q: "What if it rains?",
        a: "The cooking and meal continue indoors. The farm walk is adjusted or shortened safely.",
      },
    ],
  },

  "village-life-experience": {
    slug: "village-life-experience",
    title: "Village Life Experience",
    emoji: "🏡",
    tagline: "Discover the rhythm of the village",
    badge: "Cultural",
    heroImage:
      "https://readdy.ai/api/search-image?query=Sunset%20view%20over%20a%20South%20Indian%20village%20with%20thatched%20houses%2C%20coconut%20trees%2C%20temple%20and%20dirt%20lanes%2C%20fresh%20green%20light%2C%20painterly%20stylized%20countryside%2C%20flooded%20with%20golden%20sunrise%20light%2C%20warm%20amber%20sunrays%20and%20soft%20glow%2C%20misty%20atmospheric%20haze%2C%20deep%20rich%20shadows%2C%20vivid%20saturated%20lush%20green%2C%20high%20contrast%20cinematic%20golden%20hour%20richness%2C%20ultra%20detailed&nocache=true&width=1920&height=1080&seq=exp-village-hero&orientation=landscape",
    duration: "Full day (approx. 7 hours)",
    groupSize: "2 – 18 people",
    bestSeason: "Oct – Mar",
    location: "Traditional villages near Amalapuram",
    priceFrom: "₹1,500 / person",
    overview: [
      "Slow down and let a village set your pace for the day. There are no timetables here, only the rhythm of mornings, meals and conversations.",
      "You will walk the lanes, meet local people, taste food made in their courtyards, and hear stories that have been passed down for generations.",
      "This is a gentle, respectful peek into everyday rural life — led by people who live it, not a tour script.",
    ],
    gallery: [
      {
        src: "https://readdy.ai/api/search-image?query=Narrow%20village%20lane%20lined%20with%20coconut%20palms%20and%20simple%20houses%2C%20people%20walking%2C%20fresh%20afternoon%20light%2C%20painterly%2C%20flooded%20with%20golden%20sunrise%20light%2C%20warm%20amber%20sunrays%20and%20soft%20glow%2C%20misty%20atmospheric%20haze%2C%20deep%20rich%20shadows%2C%20vivid%20saturated%20lush%20green%2C%20high%20contrast%20cinematic%20golden%20hour%20richness%2C%20ultra%20detailed&nocache=true&width=1200&height=900&seq=exp-village-g1&orientation=landscape",
        alt: "Village lane lined with coconut palms",
      },
      {
        src: "https://readdy.ai/api/search-image?query=Elderly%20village%20man%20telling%20stories%20to%20visitors%20seated%20under%20a%20large%20banyan%20tree%2C%20dappled%20light%2C%20painterly%2C%20flooded%20with%20golden%20sunrise%20light%2C%20warm%20amber%20sunrays%20and%20soft%20glow%2C%20misty%20atmospheric%20haze%2C%20deep%20rich%20shadows%2C%20vivid%20saturated%20lush%20green%2C%20high%20contrast%20cinematic%20golden%20hour%20richness%2C%20ultra%20detailed&nocache=true&width=1200&height=900&seq=exp-village-g2&orientation=landscape",
        alt: "Elder telling stories under a banyan tree",
      },
      {
        src: "https://readdy.ai/api/search-image?query=Traditional%20South%20Indian%20village%20festival%20procession%20with%20decorated%20carts%20and%20drums%2C%20colorful%2C%20painterly%2C%20flooded%20with%20golden%20sunrise%20light%2C%20warm%20amber%20sunrays%20and%20soft%20glow%2C%20misty%20atmospheric%20haze%2C%20deep%20rich%20shadows%2C%20vivid%20saturated%20lush%20green%2C%20high%20contrast%20cinematic%20golden%20hour%20richness%2C%20ultra%20detailed&nocache=true&width=1200&height=900&seq=exp-village-g3&orientation=landscape",
        alt: "Colorful village festival procession",
      },
      {
        src: "https://readdy.ai/api/search-image?query=Local%20woman%20preparing%20fresh%20coconut%20and%20spices%20in%20a%20traditional%20courtyard%2C%20fresh%20tones%2C%20painterly%2C%20flooded%20with%20golden%20sunrise%20light%2C%20warm%20amber%20sunrays%20and%20soft%20glow%2C%20misty%20atmospheric%20haze%2C%20deep%20rich%20shadows%2C%20vivid%20saturated%20lush%20green%2C%20high%20contrast%20cinematic%20golden%20hour%20richness%2C%20ultra%20detailed&nocache=true&width=1200&height=900&seq=exp-village-g4&orientation=landscape",
        alt: "Woman preparing coconut and spices in a courtyard",
      },
      {
        src: "https://readdy.ai/api/search-image?query=Small%20wooden%20boat%20gliding%20along%20a%20calm%20canal%20beside%20a%20village%2C%20reflections%2C%20early%20morning%2C%20painterly%2C%20flooded%20with%20golden%20sunrise%20light%2C%20warm%20amber%20sunrays%20and%20soft%20glow%2C%20misty%20atmospheric%20haze%2C%20deep%20rich%20shadows%2C%20vivid%20saturated%20lush%20green%2C%20high%20contrast%20cinematic%20golden%20hour%20richness%2C%20ultra%20detailed&nocache=true&width=1200&height=900&seq=exp-village-g5&orientation=landscape",
        alt: "Wooden boat on a calm canal beside a village",
      },
    ],
    whatYouWillDo: [
      "Walk through the village lanes with a local host",
      "Visit a traditional home and see daily routines",
      "Taste food prepared fresh in a village courtyard",
      "Learn a craft, ritual or custom from the people who keep it",
      "Sit under a banyan tree and listen to village stories",
      "Take a quiet boat ride along the canal if weather allows",
    ],
    includes: [
      "Local village host and storyteller",
      "Guided village and canal walk",
      "Traditional lunch and local snacks",
      "Craft or cultural demonstration",
      "Drinking water throughout",
      "Small contribution that supports the community",
    ],
    goodToKnow: [
      "Dress modestly out of respect for local customs",
      "Always ask before photographing people or homes",
      "Some walks are on uneven village paths",
      "Activities change with festivals and seasons",
    ],
    faqs: [
      {
        q: "Is this a guided tour?",
        a: "It is a hosted experience. A local resident walks with you and opens the doors that a normal tour cannot.",
      },
      {
        q: "How much walking is involved?",
        a: "Around 3–4 km spread across the day, with plenty of breaks for food and conversation.",
      },
      {
        q: "Can we visit a temple?",
        a: "Yes, many routes include a village temple. There is a simple dress code that we will share beforehand.",
      },
    ],
  },

  "orchard-plantation-experiences": {
    slug: "orchard-plantation-experiences",
    title: "Orchard & Plantation Experiences",
    emoji: "🌴",
    tagline: "Fruits & crops up close",
    badge: "Seasonal",
    heroImage:
      "https://readdy.ai/api/search-image?query=Lush%20coconut%20and%20banana%20plantation%20in%20rural%20Andhra%20Pradesh%2C%20tall%20palms%2C%20dappled%20sunlight%2C%20lush%20green%2C%20painterly%20stylized%20tropical%20landscape%2C%20flooded%20with%20golden%20sunrise%20light%2C%20warm%20amber%20sunrays%20and%20soft%20glow%2C%20misty%20atmospheric%20haze%2C%20deep%20rich%20shadows%2C%20vivid%20saturated%20lush%20green%2C%20high%20contrast%20cinematic%20golden%20hour%20richness%2C%20ultra%20detailed&nocache=true&width=1920&height=1080&seq=exp-orchard-hero&orientation=landscape",
    duration: "Half day (approx. 4 hours)",
    groupSize: "2 – 30 people",
    bestSeason: "Seasonal harvest windows",
    location: "Coconut groves & mango orchards, Konaseema",
    priceFrom: "₹1,400 / person",
    overview: [
      "Walk through tall coconut groves and mango orchards and see how your favourite fruits are actually grown.",
      "You will learn how a plantation is planted, watered and cared for, and hear the farmer's own story of the trees he tends.",
      "Depending on the season, you can plant, harvest and taste fruit picked by your own hands — nothing tastes quite like it.",
    ],
    gallery: [
      {
        src: "https://readdy.ai/api/search-image?query=Rows%20of%20coconut%20palms%20in%20a%20sunlit%20plantation%20with%20a%20farmer%20inspecting%20trees%2C%20painterly%20green%20tones%2C%20flooded%20with%20golden%20sunrise%20light%2C%20warm%20amber%20sunrays%20and%20soft%20glow%2C%20misty%20atmospheric%20haze%2C%20deep%20rich%20shadows%2C%20vivid%20saturated%20lush%20green%2C%20high%20contrast%20cinematic%20golden%20hour%20richness%2C%20ultra%20detailed&nocache=true&width=1200&height=900&seq=exp-orchard-g1&orientation=landscape",
        alt: "Rows of coconut palms in a plantation",
      },
      {
        src: "https://readdy.ai/api/search-image?query=Freshly%20harvested%20bananas%20and%20mangoes%20in%20baskets%20on%20plantation%20ground%2C%20fresh%20light%2C%20painterly%2C%20flooded%20with%20golden%20sunrise%20light%2C%20warm%20amber%20sunrays%20and%20soft%20glow%2C%20misty%20atmospheric%20haze%2C%20deep%20rich%20shadows%2C%20vivid%20saturated%20lush%20green%2C%20high%20contrast%20cinematic%20golden%20hour%20richness%2C%20ultra%20detailed&nocache=true&width=1200&height=900&seq=exp-orchard-g2&orientation=landscape",
        alt: "Baskets of harvested bananas and mangoes",
      },
      {
        src: "https://readdy.ai/api/search-image?query=Visitor%20reaching%20up%20to%20pick%20ripe%20mango%20from%20a%20laden%20orchard%20tree%2C%20sunny%2C%20painterly%2C%20flooded%20with%20golden%20sunrise%20light%2C%20warm%20amber%20sunrays%20and%20soft%20glow%2C%20misty%20atmospheric%20haze%2C%20deep%20rich%20shadows%2C%20vivid%20saturated%20lush%20green%2C%20high%20contrast%20cinematic%20golden%20hour%20richness%2C%20ultra%20detailed&nocache=true&width=1200&height=900&seq=exp-orchard-g3&orientation=landscape",
        alt: "Visitor picking ripe mango from an orchard tree",
      },
      {
        src: "https://readdy.ai/api/search-image?query=Close%20up%20of%20flowering%20banana%20plant%20and%20young%20fruit%20in%20a%20plantation%2C%20soft%20light%2C%20painterly%2C%20flooded%20with%20golden%20sunrise%20light%2C%20warm%20amber%20sunrays%20and%20soft%20glow%2C%20misty%20atmospheric%20haze%2C%20deep%20rich%20shadows%2C%20vivid%20saturated%20lush%20green%2C%20high%20contrast%20cinematic%20golden%20hour%20richness%2C%20ultra%20detailed&nocache=true&width=1200&height=900&seq=exp-orchard-g4&orientation=landscape",
        alt: "Flowering banana plant with young fruit",
      },
      {
        src: "https://readdy.ai/api/search-image?query=Orchard%20pathway%20with%20mango%20trees%20and%20a%20bamboo%20fence%2C%20bright%20afternoon%20light%2C%20painterly%2C%20flooded%20with%20golden%20sunrise%20light%2C%20warm%20amber%20sunrays%20and%20soft%20glow%2C%20misty%20atmospheric%20haze%2C%20deep%20rich%20shadows%2C%20vivid%20saturated%20lush%20green%2C%20high%20contrast%20cinematic%20golden%20hour%20richness%2C%20ultra%20detailed&nocache=true&width=1200&height=900&seq=exp-orchard-g5&orientation=landscape",
        alt: "Orchard pathway with mango trees",
      },
    ],
    whatYouWillDo: [
      "Walk covered groves and orchards with the farmer",
      "Learn how planting, pruning and watering really work",
      "Taste fruit and tender coconut fresh from the tree",
      "Join a harvest if it is the right season",
      "Hear the story behind the plantation and its generations",
      "Learn which fruits grow in which season",
    ],
    includes: [
      "Farmer-guided orchard and grove tour",
      "Fresh fruit and tender coconut tasting",
      "Seasonal harvest participation",
      "Shade and rest areas along the route",
      "Drinking water",
      "Jute bag to carry fruit home",
    ],
    goodToKnow: [
      "Harvest activities depend entirely on the season",
      "Wear closed shoes and light clothing",
      "Grove walks are mostly on flat ground",
      "Mosquito repellent is useful in the monsoon",
    ],
    faqs: [
      {
        q: "Will we definitely harvest fruit?",
        a: "Only if your visit falls in a harvest window. We will tell you in advance what is ready during your dates.",
      },
      {
        q: "Is the walk shaded?",
        a: "Yes, coconut groves and mature orchards provide plenty of natural shade.",
      },
      {
        q: "Can we buy fruit to take home?",
        a: "Absolutely. You can buy freshly harvested fruit directly from the farmer and support them directly.",
      },
    ],
  },

  "family-farm-experiences": {
    slug: "family-farm-experiences",
    title: "Family Farm Experiences",
    emoji: "👨‍👩‍👧",
    tagline: "Learning beyond the classroom",
    badge: "For Families",
    heroImage:
      "https://readdy.ai/api/search-image?query=Happy%20Indian%20family%20with%20children%20exploring%20a%20green%20farm%2C%20kids%20touching%20plants%2C%20parents%20smiling%2C%20fresh%20sunny%20countryside%2C%20painterly%20earthy%20tones%2C%20flooded%20with%20golden%20sunrise%20light%2C%20warm%20amber%20sunrays%20and%20soft%20glow%2C%20misty%20atmospheric%20haze%2C%20deep%20rich%20shadows%2C%20vivid%20saturated%20lush%20green%2C%20high%20contrast%20cinematic%20golden%20hour%20richness%2C%20ultra%20detailed&nocache=true&width=1920&height=1080&seq=exp-family-hero&orientation=landscape",
    duration: "Half day (approx. 4.5 hours)",
    groupSize: "Families (up to 6 per group)",
    bestSeason: "Weekends, year-round",
    location: "Family-friendly farms in Konaseema",
    priceFrom: "₹1,200 / adult",
    overview: [
      "Give your children something no screen can offer — real soil, real animals, and the simple wonder of seeing where food comes from.",
      "This experience is designed at a child's pace, with hands-on activities that keep little ones curious and busy from start to finish.",
      "Parents get to slow down too, sharing a wholesome farm meal and watching their kids learn, play and get properly muddy.",
    ],
    gallery: [
      {
        src: "https://readdy.ai/api/search-image?query=Two%20children%20joyfully%20getting%20hands%20muddy%20in%20farm%20soil%20while%20planting%20a%20sapling%2C%20bright%20sunny%2C%20painterly%2C%20flooded%20with%20golden%20sunrise%20light%2C%20warm%20amber%20sunrays%20and%20soft%20glow%2C%20misty%20atmospheric%20haze%2C%20deep%20rich%20shadows%2C%20vivid%20saturated%20lush%20green%2C%20high%20contrast%20cinematic%20golden%20hour%20richness%2C%20ultra%20detailed&nocache=true&width=1200&height=900&seq=exp-family-g1&orientation=landscape",
        alt: "Children planting a sapling with muddy hands",
      },
      {
        src: "https://readdy.ai/api/search-image?query=Child%20feeding%20and%20petting%20a%20calf%20and%20hens%20at%20a%20village%20farm%2C%20fresh%20light%2C%20painterly%2C%20flooded%20with%20golden%20sunrise%20light%2C%20warm%20amber%20sunrays%20and%20soft%20glow%2C%20misty%20atmospheric%20haze%2C%20deep%20rich%20shadows%2C%20vivid%20saturated%20lush%20green%2C%20high%20contrast%20cinematic%20golden%20hour%20richness%2C%20ultra%20detailed&nocache=true&width=1200&height=900&seq=exp-family-g2&orientation=landscape",
        alt: "Child feeding a calf at the farm",
      },
      {
        src: "https://readdy.ai/api/search-image?query=Family%20walking%20together%20along%20a%20green%20farm%20path%20with%20baskets%2C%20sunny%20day%2C%20painterly%2C%20flooded%20with%20golden%20sunrise%20light%2C%20warm%20amber%20sunrays%20and%20soft%20glow%2C%20misty%20atmospheric%20haze%2C%20deep%20rich%20shadows%2C%20vivid%20saturated%20lush%20green%2C%20high%20contrast%20cinematic%20golden%20hour%20richness%2C%20ultra%20detailed&nocache=true&width=1200&height=900&seq=exp-family-g3&orientation=landscape",
        alt: "Family walking along a green farm path",
      },
      {
        src: "https://readdy.ai/api/search-image?query=Small%20child%20holding%20a%20fresh%20carrot%20pulled%20from%20the%20soil%2C%20amazed%20expression%2C%20painterly%2C%20flooded%20with%20golden%20sunrise%20light%2C%20warm%20amber%20sunrays%20and%20soft%20glow%2C%20misty%20atmospheric%20haze%2C%20deep%20rich%20shadows%2C%20vivid%20saturated%20lush%20green%2C%20high%20contrast%20cinematic%20golden%20hour%20richness%2C%20ultra%20detailed&nocache=true&width=1200&height=900&seq=exp-family-g4&orientation=landscape",
        alt: "Child holding a freshly pulled carrot",
      },
      {
        src: "https://readdy.ai/api/search-image?query=Family%20picnicking%20on%20a%20woven%20mat%20under%20a%20tree%20at%20a%20farm%2C%20traditional%20food%2C%20fresh%20light%2C%20painterly%2C%20flooded%20with%20golden%20sunrise%20light%2C%20warm%20amber%20sunrays%20and%20soft%20glow%2C%20misty%20atmospheric%20haze%2C%20deep%20rich%20shadows%2C%20vivid%20saturated%20lush%20green%2C%20high%20contrast%20cinematic%20golden%20hour%20richness%2C%20ultra%20detailed&nocache=true&width=1200&height=900&seq=exp-family-g5&orientation=landscape",
        alt: "Family picnicking under a tree at the farm",
      },
    ],
    whatYouWillDo: [
      "Plant a sapling your child can name and remember",
      "Meet and feed farm animals like calves, goats and hens",
      "Play a soil and seed discovery activity",
      "Pull vegetables from the ground together",
      "Enjoy a relaxed family picnic on the farm",
      "Take home a little seedling to grow",
    ],
    includes: [
      "Family-friendly farm host",
      "All hands-on activities and materials",
      "Sapling to plant and take home",
      "Wholesome family meal and snacks",
      "Safe, shaded play areas",
      "Drinking water and wash areas",
    ],
    goodToKnow: [
      "Designed for children from age 3 upwards",
      "Bring a spare set of clothes for the kids",
      "Guardians stay with children at all times",
      "Nappies and basic child care support available",
    ],
    faqs: [
      {
        q: "Is it safe for small children?",
        a: "Yes, the activities are gentle, supervised and run on safe, flat, fenced areas of the farm.",
      },
      {
        q: "Do parents pay too?",
        a: "Yes, the ticket covers adults and children, and includes the meal and all activities for everyone.",
      },
      {
        q: "How long is the experience?",
        a: "About 4.5 hours, timed so children don't get overtired, with a long break for the meal.",
      },
    ],
  },

  "school-educational-farm-visits": {
    slug: "school-educational-farm-visits",
    title: "School & Educational Farm Visits",
    emoji: "🏫",
    tagline: "From textbook to real world",
    badge: "For Schools",
    heroImage:
      "https://readdy.ai/api/search-image?query=Group%20of%20Indian%20school%20children%20in%20uniform%20exploring%20a%20green%20farm%20with%20a%20teacher%2C%20curious%20and%20smiling%2C%20sunny%20countryside%2C%20painterly%20stylized%2C%20flooded%20with%20golden%20sunrise%20light%2C%20warm%20amber%20sunrays%20and%20soft%20glow%2C%20misty%20atmospheric%20haze%2C%20deep%20rich%20shadows%2C%20vivid%20saturated%20lush%20green%2C%20high%20contrast%20cinematic%20golden%20hour%20richness%2C%20ultra%20detailed&nocache=true&width=1920&height=1080&seq=exp-school-hero&orientation=landscape",
    duration: "Curriculum modules (3 – 6 hours)",
    groupSize: "20 – 120 students",
    bestSeason: "Jul – Feb (school terms)",
    location: "Partner farms across Konaseema",
    priceFrom: "₹650 / student",
    overview: [
      "Agriculture is understood best when it is experienced, not memorised. Our school visits turn the syllabus into a living, breathing classroom.",
      "Students move through structured modules — soil, crops, irrigation and sustainability — led by farmers and trained facilitators.",
      "Every trip ends with reflection and a simple takeaway, so learning continues long after the bus ride home.",
    ],
    gallery: [
      {
        src: "https://readdy.ai/api/search-image?query=School%20students%20sitting%20in%20a%20green%20field%20listening%20to%20a%20farmer%20teaching%20about%20crops%2C%20painterly%2C%20flooded%20with%20golden%20sunrise%20light%2C%20warm%20amber%20sunrays%20and%20soft%20glow%2C%20misty%20atmospheric%20haze%2C%20deep%20rich%20shadows%2C%20vivid%20saturated%20lush%20green%2C%20high%20contrast%20cinematic%20golden%20hour%20richness%2C%20ultra%20detailed&nocache=true&width=1200&height=900&seq=exp-school-g1&orientation=landscape",
        alt: "Students listening to a farmer in a green field",
      },
      {
        src: "https://readdy.ai/api/search-image?query=Children%20learning%20about%20irrigation%20channels%20and%20water%20flow%20in%20a%20farm%2C%20curious%20faces%2C%20painterly%2C%20flooded%20with%20golden%20sunrise%20light%2C%20warm%20amber%20sunrays%20and%20soft%20glow%2C%20misty%20atmospheric%20haze%2C%20deep%20rich%20shadows%2C%20vivid%20saturated%20lush%20green%2C%20high%20contrast%20cinematic%20golden%20hour%20richness%2C%20ultra%20detailed&nocache=true&width=1200&height=900&seq=exp-school-g2&orientation=landscape",
        alt: "Children learning about irrigation channels",
      },
      {
        src: "https://readdy.ai/api/search-image?query=Students%20observing%20farm%20animals%20like%20cows%20and%20goats%20in%20a%20village%20shed%2C%20painterly%2C%20flooded%20with%20golden%20sunrise%20light%2C%20warm%20amber%20sunrays%20and%20soft%20glow%2C%20misty%20atmospheric%20haze%2C%20deep%20rich%20shadows%2C%20vivid%20saturated%20lush%20green%2C%20high%20contrast%20cinematic%20golden%20hour%20richness%2C%20ultra%20detailed&nocache=true&width=1200&height=900&seq=exp-school-g3&orientation=landscape",
        alt: "Students observing farm animals",
      },
      {
        src: "https://readdy.ai/api/search-image?query=Kids%20planting%20saplings%20in%20a%20neat%20row%20during%20a%20farm%20education%20visit%2C%20sunny%2C%20painterly%2C%20flooded%20with%20golden%20sunrise%20light%2C%20warm%20amber%20sunrays%20and%20soft%20glow%2C%20misty%20atmospheric%20haze%2C%20deep%20rich%20shadows%2C%20vivid%20saturated%20lush%20green%2C%20high%20contrast%20cinematic%20golden%20hour%20richness%2C%20ultra%20detailed&nocache=true&width=1200&height=900&seq=exp-school-g4&orientation=landscape",
        alt: "Kids planting saplings in a row",
      },
      {
        src: "https://readdy.ai/api/search-image?query=Students%20taking%20notes%20and%20sketching%20plants%20in%20a%20farm%20field%2C%20natural%20light%2C%20painterly%2C%20flooded%20with%20golden%20sunrise%20light%2C%20warm%20amber%20sunrays%20and%20soft%20glow%2C%20misty%20atmospheric%20haze%2C%20deep%20rich%20shadows%2C%20vivid%20saturated%20lush%20green%2C%20high%20contrast%20cinematic%20golden%20hour%20richness%2C%20ultra%20detailed&nocache=true&width=1200&height=900&seq=exp-school-g5&orientation=landscape",
        alt: "Students sketching plants in a farm field",
      },
    ],
    whatYouWillDo: [
      "Module on soil, its layers and why it matters",
      "Crop cultivation walk with a working farmer",
      "Live demonstration of irrigation and water management",
      "Introduction to sustainable and organic farming",
      "Hands-on planting or harvesting activity",
      "Guided reflection and learning worksheet",
    ],
    includes: [
      "Trained facilitators and farmer educators",
      "Structured learning modules and worksheets",
      "Hands-on activity materials",
      "Mid-day meal and drinking water",
      "First-aid support on site",
      "Teacher briefing pack beforehand",
    ],
    goodToKnow: [
      "Minimum 20 students per visit",
      "One teacher is required per 15 students",
      "Group sizes can be split into rotating batches",
      "We can align modules to your syllabus on request",
    ],
    faqs: [
      {
        q: "Can you handle very large groups?",
        a: "Yes, up to 120 students. We split them into rotating batches so every child participates fully.",
      },
      {
        q: "Do you provide transport?",
        a: "We don't arrange buses, but we help with route planning and share a map with the driver.",
      },
      {
        q: "Is the content age-appropriate?",
        a: "Yes, modules are adjusted for primary, middle and high school levels.",
      },
    ],
  },

  "corporate-rural-experiences": {
    slug: "corporate-rural-experiences",
    title: "Corporate Rural Experiences",
    emoji: "🏢",
    tagline: "Beyond the office",
    badge: "For Teams",
    heroImage:
      "https://readdy.ai/api/search-image?query=Corporate%20team%20in%20casual%20clothes%20participating%20in%20a%20rural%20farm%20team%20activity%20in%20India%2C%20laughing%20and%20working%20together%20in%20green%20fields%2C%20sunny%2C%20painterly%2C%20flooded%20with%20golden%20sunrise%20light%2C%20warm%20amber%20sunrays%20and%20soft%20glow%2C%20misty%20atmospheric%20haze%2C%20deep%20rich%20shadows%2C%20vivid%20saturated%20lush%20green%2C%20high%20contrast%20cinematic%20golden%20hour%20richness%2C%20ultra%20detailed&nocache=true&width=1920&height=1080&seq=exp-corporate-hero&orientation=landscape",
    duration: "Custom (1 – 3 days)",
    groupSize: "15 – 150 people",
    bestSeason: "Nov – Feb",
    location: "Rural venues across Konaseema",
    priceFrom: "On request",
    overview: [
      "Take your team somewhere that resets everyone's perspective. Rythugaru designs rural experiences that combine nature, agriculture, local food and genuine human connection.",
      "Sessions are built around shared work, honest conversation and a slower rhythm — the kind of environment where teams actually talk to each other.",
      "Choose from team outings, employee engagement, leadership retreats, CSR programs and multi-day rural immersions.",
    ],
    gallery: [
      {
        src: "https://readdy.ai/api/search-image?query=Diverse%20corporate%20team%20planting%20saplings%20together%20on%20a%20farm%2C%20teamwork%2C%20fresh%20light%2C%20painterly%2C%20flooded%20with%20golden%20sunrise%20light%2C%20warm%20amber%20sunrays%20and%20soft%20glow%2C%20misty%20atmospheric%20haze%2C%20deep%20rich%20shadows%2C%20vivid%20saturated%20lush%20green%2C%20high%20contrast%20cinematic%20golden%20hour%20richness%2C%20ultra%20detailed&nocache=true&width=1200&height=900&seq=exp-corporate-g1&orientation=landscape",
        alt: "Corporate team planting saplings together",
      },
      {
        src: "https://readdy.ai/api/search-image?query=Office%20colleagues%20enjoying%20a%20traditional%20rural%20meal%20together%20on%20a%20long%20clay%20table%2C%20painterly%2C%20flooded%20with%20golden%20sunrise%20light%2C%20warm%20amber%20sunrays%20and%20soft%20glow%2C%20misty%20atmospheric%20haze%2C%20deep%20rich%20shadows%2C%20vivid%20saturated%20lush%20green%2C%20high%20contrast%20cinematic%20golden%20hour%20richness%2C%20ultra%20detailed&nocache=true&width=1200&height=900&seq=exp-corporate-g2&orientation=landscape",
        alt: "Colleagues sharing a traditional rural meal",
      },
      {
        src: "https://readdy.ai/api/search-image?query=Corporate%20group%20on%20a%20rural%20immersion%20walk%20through%20paddy%20fields%20with%20a%20farmer%20guide%2C%20painterly%2C%20flooded%20with%20golden%20sunrise%20light%2C%20warm%20amber%20sunrays%20and%20soft%20glow%2C%20misty%20atmospheric%20haze%2C%20deep%20rich%20shadows%2C%20vivid%20saturated%20lush%20green%2C%20high%20contrast%20cinematic%20golden%20hour%20richness%2C%20ultra%20detailed&nocache=true&width=1200&height=900&seq=exp-corporate-g3&orientation=landscape",
        alt: "Corporate group walking through paddy fields",
      },
      {
        src: "https://readdy.ai/api/search-image?query=Team%20building%20tug%20of%20war%20and%20games%20in%20a%20village%20field%2C%20joyful%2C%20painterly%2C%20flooded%20with%20golden%20sunrise%20light%2C%20warm%20amber%20sunrays%20and%20soft%20glow%2C%20misty%20atmospheric%20haze%2C%20deep%20rich%20shadows%2C%20vivid%20saturated%20lush%20green%2C%20high%20contrast%20cinematic%20golden%20hour%20richness%2C%20ultra%20detailed&nocache=true&width=1200&height=900&seq=exp-corporate-g4&orientation=landscape",
        alt: "Team building games in a village field",
      },
      {
        src: "https://readdy.ai/api/search-image?query=Employees%20sitting%20in%20a%20circle%20under%20a%20tree%20doing%20a%20discussion%20session%20at%20a%20farm%2C%20painterly%2C%20flooded%20with%20golden%20sunrise%20light%2C%20warm%20amber%20sunrays%20and%20soft%20glow%2C%20misty%20atmospheric%20haze%2C%20deep%20rich%20shadows%2C%20vivid%20saturated%20lush%20green%2C%20high%20contrast%20cinematic%20golden%20hour%20richness%2C%20ultra%20detailed&nocache=true&width=1200&height=900&seq=exp-corporate-g5&orientation=landscape",
        alt: "Employees in a discussion circle under a tree",
      },
    ],
    whatYouWillDo: [
      "Rural immersion walk with a farmer host",
      "Team activity built around shared farm work",
      "Farm-to-table meal cooked and shared together",
      "Facilitated team discussion in a natural setting",
      "Optional CSR community or planting project",
      "Optional evening cultural program",
    ],
    includes: [
      "Custom itinerary designed with your team",
      "Dedicated on-ground coordinator",
      "All activities, materials and facilitation",
      "Meals, snacks and refreshments",
      "Basic amenities and washrooms",
      "Optional transport coordination",
    ],
    goodToKnow: [
      "Minimum 15 participants",
      "Group sizes split into activity pods",
      "Fully customisable to your objectives",
      "Advance booking of 2–3 weeks recommended",
    ],
    faqs: [
      {
        q: "Can you handle very large groups?",
        a: "Yes, we regularly host groups up to 150 people by splitting them into rotating activity pods.",
      },
      {
        q: "Do you provide transport?",
        a: "We coordinate closely with your travel partner and can recommend vans and buses.",
      },
      {
        q: "Can this tie into our CSR goals?",
        a: "Yes, we can connect your team with a real community or tree-planting project that leaves a lasting impact.",
      },
    ],
  },

  "rural-culture-traditions": {
    slug: "rural-culture-traditions",
    title: "Rural Culture & Traditions",
    emoji: "🎨",
    tagline: "Customs, crafts & festivals",
    badge: "Culture",
    heroImage:
      "https://readdy.ai/api/search-image?query=Colorful%20South%20Indian%20village%20festival%20with%20folk%20dancers%20in%20traditional%20attire%2C%20decorated%20surroundings%2C%20vibrant%20earthy%20tones%2C%20painterly%20stylized%2C%20flooded%20with%20golden%20sunrise%20light%2C%20warm%20amber%20sunrays%20and%20soft%20glow%2C%20misty%20atmospheric%20haze%2C%20deep%20rich%20shadows%2C%20vivid%20saturated%20lush%20green%2C%20high%20contrast%20cinematic%20golden%20hour%20richness%2C%20ultra%20detailed&nocache=true&width=1920&height=1080&seq=exp-culture-hero&orientation=landscape",
    duration: "Half to full day",
    groupSize: "2 – 25 people",
    bestSeason: "Festival season (Sep – Jan)",
    location: "Craft villages & temple towns, Konaseema",
    priceFrom: "₹1,700 / person",
    overview: [
      "Immerse yourself in the living culture of rural India — the crafts, music, festivals and everyday traditions kept alive across generations.",
      "You will meet artisans at their work, learn a traditional craft with your own hands, and hear folk stories from the elders who hold them.",
      "Depending on the season, your visit may coincide with a village festival or temple celebration, which is the very best time to be here.",
    ],
    gallery: [
      {
        src: "https://readdy.ai/api/search-image?query=Artisan%20weaving%20traditional%20handloom%20fabric%20in%20a%20village%20workshop%2C%20fresh%20light%2C%20painterly%2C%20flooded%20with%20golden%20sunrise%20light%2C%20warm%20amber%20sunrays%20and%20soft%20glow%2C%20misty%20atmospheric%20haze%2C%20deep%20rich%20shadows%2C%20vivid%20saturated%20lush%20green%2C%20high%20contrast%20cinematic%20golden%20hour%20richness%2C%20ultra%20detailed&nocache=true&width=1200&height=900&seq=exp-culture-g1&orientation=landscape",
        alt: "Artisan weaving on a traditional handloom",
      },
      {
        src: "https://readdy.ai/api/search-image?query=Village%20potter%20shaping%20clay%20pots%20on%20a%20wheel%20with%20hands%2C%20earthy%20tones%2C%20painterly%2C%20flooded%20with%20golden%20sunrise%20light%2C%20warm%20amber%20sunrays%20and%20soft%20glow%2C%20misty%20atmospheric%20haze%2C%20deep%20rich%20shadows%2C%20vivid%20saturated%20lush%20green%2C%20high%20contrast%20cinematic%20golden%20hour%20richness%2C%20ultra%20detailed&nocache=true&width=1200&height=900&seq=exp-culture-g2&orientation=landscape",
        alt: "Village potter shaping clay on a wheel",
      },
      {
        src: "https://readdy.ai/api/search-image?query=Colorful%20rural%20handicrafts%20and%20terracotta%20items%20displayed%20on%20a%20woven%20mat%2C%20painterly%2C%20flooded%20with%20golden%20sunrise%20light%2C%20warm%20amber%20sunrays%20and%20soft%20glow%2C%20misty%20atmospheric%20haze%2C%20deep%20rich%20shadows%2C%20vivid%20saturated%20lush%20green%2C%20high%20contrast%20cinematic%20golden%20hour%20richness%2C%20ultra%20detailed&nocache=true&width=1200&height=900&seq=exp-culture-g3&orientation=landscape",
        alt: "Colorful rural handicrafts on a woven mat",
      },
      {
        src: "https://readdy.ai/api/search-image?query=Folk%20dancers%20in%20vibrant%20traditional%20costumes%20performing%20at%20a%20village%20festival%2C%20painterly%2C%20flooded%20with%20golden%20sunrise%20light%2C%20warm%20amber%20sunrays%20and%20soft%20glow%2C%20misty%20atmospheric%20haze%2C%20deep%20rich%20shadows%2C%20vivid%20saturated%20lush%20green%2C%20high%20contrast%20cinematic%20golden%20hour%20richness%2C%20ultra%20detailed&nocache=true&width=1200&height=900&seq=exp-culture-g4&orientation=landscape",
        alt: "Folk dancers performing at a village festival",
      },
      {
        src: "https://readdy.ai/api/search-image?query=Woman%20drawing%20intricate%20decorative%20patterns%20in%20a%20village%20courtyard%2C%20painterly%2C%20flooded%20with%20golden%20sunrise%20light%2C%20warm%20amber%20sunrays%20and%20soft%20glow%2C%20misty%20atmospheric%20haze%2C%20deep%20rich%20shadows%2C%20vivid%20saturated%20lush%20green%2C%20high%20contrast%20cinematic%20golden%20hour%20richness%2C%20ultra%20detailed&nocache=true&width=1200&height=900&seq=exp-culture-g5&orientation=landscape",
        alt: "Woman drawing decorative patterns in a courtyard",
      },
    ],
    whatYouWillDo: [
      "Meet artisans weaving, potting and crafting by hand",
      "Try a traditional craft yourself as a keepsake",
      "Learn about festivals and their meanings",
      "Listen to folk songs and village storytelling",
      "Visit a temple or heritage village site",
      "Taste festive foods of the season",
    ],
    includes: [
      "Cultural host and artisan introductions",
      "Hands-on craft session with materials",
      "Folk performance or storytelling",
      "Traditional festive meal",
      "Temple or heritage visit",
      "Craft item to take home",
    ],
    goodToKnow: [
      "Festival availability varies by season",
      "Respect local customs and dress codes",
      "Photography is allowed only with consent",
      "Some artisans' workshops are small; groups rotate",
    ],
    faqs: [
      {
        q: "Can we join during a festival?",
        a: "Yes, and it is the highlight of the year. Tell us your dates and we will match you to a local festival if possible.",
      },
      {
        q: "Is the craft session suitable for adults?",
        a: "Absolutely — the artisans adapt the session for all ages and skill levels.",
      },
      {
        q: "Can we buy authentic crafts directly?",
        a: "Yes, buying directly from artisans is encouraged and supports their craft.",
      },
    ],
  },
};